import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from './UserContext';
import styled from 'styled-components';
import theme from '../theme';

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: ${theme.colors.bgSecondary};
  border-bottom: 2px solid ${theme.colors.borderBright};
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 0 20px rgba(0, 255, 65, 0.3);
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column;
    gap: 1rem;
  }
`;

const Logo = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${theme.colors.text};
  font-family: ${theme.fonts.mono};
  cursor: pointer;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: ${theme.shadows.glow};
  transition: all ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.primaryLight};
    text-shadow: ${theme.shadows.glowStrong};
  }

  &::before {
    content: '> ';
    color: ${theme.colors.primary};
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
  }
`;

const NavButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.text};
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  position: relative;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 1px;

  &::before {
    content: '> ';
    color: ${theme.colors.primary};
    opacity: 0;
    transition: opacity ${theme.transitions.fast};
  }

  &:hover {
    color: ${theme.colors.primaryLight};
    
    &::before {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.4rem 0.8rem;
  }
`;

const ActionButton = styled.button`
  background: transparent;
  border: 1px solid ${theme.colors.borderBright};
  color: ${theme.colors.text};
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  text-transform: uppercase;
  letter-spacing: 1px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: ${theme.colors.overlayLight};
    transition: left ${theme.transitions.normal};
  }

  &:hover {
    background: ${theme.colors.overlayLight};
    box-shadow: ${theme.shadows.glow};
    border-color: ${theme.colors.primaryLight};
    
    &::before {
      left: 100%;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
    margin-top: 0.5rem;
  }
`;

const ProfileContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled(NavButton)`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &::after {
    content: ' ▼';
    font-size: 0.7rem;
    color: ${theme.colors.textMuted};
  }
`;

const DropdownContent = styled.div`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: absolute;
  background: ${theme.colors.bgTertiary};
  min-width: 250px;
  box-shadow: ${theme.shadows.glow};
  z-index: 1;
  border-radius: ${theme.borderRadius.md};
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  border: 1px solid ${theme.colors.borderBright};
  overflow: hidden;
`;

const DropdownItem = styled.button`
  color: ${theme.colors.text};
  padding: 12px 16px;
  text-decoration: none;
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  transition: all ${theme.transitions.fast};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &::before {
    content: '> ';
    color: ${theme.colors.primary};
    opacity: 0;
    transition: opacity ${theme.transitions.fast};
  }
  
  &:hover {
    background: ${theme.colors.overlayLight};
    color: ${theme.colors.primaryLight};
    
    &::before {
      opacity: 1;
    }
  }
`;

const Header = () => {
  const navigate = useNavigate();
  const { user, setUser } = useUser();
  const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleNavigation = (path) => () => {
    navigate(path);
    setToolsDropdownOpen(false);
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    navigate('/');
  };

  const toggleToolsDropdown = () => {
    setToolsDropdownOpen(!toolsDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setToolsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <HeaderContainer>
      <Logo onClick={handleNavigation('/')}>
        QUANTDELTA
      </Logo>
      <Nav>
        {user && (
          <DropdownContainer ref={dropdownRef}>
            <DropdownButton onClick={toggleToolsDropdown}>
              Platform
            </DropdownButton>
            <DropdownContent isOpen={toolsDropdownOpen}>
              <DropdownItem onClick={handleNavigation('/dashboard')}>
                Dashboard
              </DropdownItem>
              <DropdownItem onClick={handleNavigation('/cryptography')}>
                Cryptography
              </DropdownItem>
              <DropdownItem onClick={handleNavigation('/address-validator')}>
                Address Validator
              </DropdownItem>
              <DropdownItem onClick={handleNavigation('/recent-news')}>
                Market News
              </DropdownItem>
              <DropdownItem onClick={handleNavigation('/economic-events')}>
                Economic Events
              </DropdownItem>
              <DropdownItem onClick={handleNavigation('/screeners')}>
                Screeners
              </DropdownItem>
              <DropdownItem onClick={handleNavigation('/ohlcv')}>
                OHLCV Data
              </DropdownItem>
            </DropdownContent>
          </DropdownContainer>
        )}
        <NavButton onClick={handleNavigation('/pricing')}>
          Pricing
        </NavButton>
        {user ? (
          <ProfileContainer>
            <NavButton onClick={handleNavigation('/dashboard')}>
              Dashboard
            </NavButton>
            <ActionButton onClick={handleLogout}>
              Logout
            </ActionButton>
          </ProfileContainer>
        ) : (
          <ActionButton onClick={handleNavigation('/signin')}>
            Sign In
          </ActionButton>
        )}
      </Nav>
    </HeaderContainer>
  );
};

export default Header;
